import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';

interface Props {
  repoId: string;
  sourceSha: string;
  onClose: () => void;
  onCreated?: (tagName: string) => void;
}

export function CreateTagModal({ repoId, sourceSha, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.createTag(repoId, name, sourceSha, message.trim() || undefined),
    onSuccess: async (tag) => {
      await qc.invalidateQueries({ queryKey: ['graph', repoId] });
      // Un nouveau tag peut déclencher un pipeline GitLab pour la branche qu'il vise (voir
      // fetchLatestPipelineStages, gitlab.ts) — sans repasser par ces deux clés, un menu contextuel
      // de branche déjà ouvert ou l'animation "pipeline actif" du graphe resteraient sur leur
      // dernière valeur jusqu'à expiration naturelle de leur staleTime (jusqu'à 30s/10s). On ne
      // connaît pas la branche ciblée ici (seulement sourceSha) : invalidation par préfixe de repo,
      // qui matche toutes les branches déjà montées plutôt qu'une seule.
      await qc.invalidateQueries({ queryKey: ['pipeline-status', repoId] });
      await qc.invalidateQueries({ queryKey: ['active-pipelines', repoId] });
      onCreated?.(tag.name);
      onClose();
    },
  });

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className="modal">
        <h2>Nouveau tag</h2>
        <p>
          Depuis : <code>{sourceSha.slice(0, 8)}</code>
        </p>
        <label className="col" style={{ gap: 4 }}>
          <span>Nom du tag</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="1.2.3"
            autoFocus
          />
        </label>
        <label className="col" style={{ gap: 4, marginTop: 8 }}>
          <span>Message (optionnel)</span>
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Laisser vide pour un tag léger, renseigner pour un tag annoté"
          />
        </label>
        <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            disabled={name.trim().length === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Créer le tag
          </button>
          <button className="secondary" onClick={onClose}>
            Annuler
          </button>
        </div>
        {mutation.isError && (
          <p className="badge error">Erreur : {(mutation.error as Error).message}</p>
        )}
      </div>
    </div>
  );
}
